import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { buildChildrenMap, buildParentMap } from '@/components/shared/Standards/standardsTreeUtils';
import { ensureChildrenFullyLoaded } from './useStandardsTree';
import {
  CheckboxState,
  explodedLevelParentIds,
  getCheckboxState,
  nearestCheckedAncestor,
} from '../standardsFilterUtils';
import useCorrelationsStore from '../useCorrelationsStore';

interface UseStandardsSelectionParams {
  grade: string | number | null;
  programId: number;
  stateId: number;
}

// Fresh read, not a memo of the subscribed nodes: handleCheck can merge newly fetched nodes into
// the store mid-handler, and a memo would not recompute until the next render.
const buildMaps = () => {
  const nodes = Object.values(useCorrelationsStore.getState().standardsNodes);

  return { childrenMap: buildChildrenMap(nodes), parentMap: buildParentMap(nodes) };
};

// Checkbox state + toggling for the standards tree. Unchecking a node under a checked ancestor
// replaces that ancestor's check with explicit checks on its other descendants — which is only
// lossless once every child list along the path is fully loaded, so any remaining "See more"
// pages are fetched first. If that fetch fails, the uncheck is skipped (applying it would silently
// drop the unloaded siblings) and checkFailed is set for the caller to surface.
const useStandardsSelection = ({ grade, programId, stateId }: UseStandardsSelectionParams) => {
  const checkedIds = useCorrelationsStore(s => s.standardsCheckedIds);
  const nodesById = useCorrelationsStore(s => s.standardsNodes);
  const checkStandard = useCorrelationsStore(s => s.checkStandard);
  const queryClient = useQueryClient();
  const [checkFailed, setCheckFailed] = useState(false);

  const maps = useMemo(() => {
    const nodes = Object.values(nodesById);

    return { childrenMap: buildChildrenMap(nodes), parentMap: buildParentMap(nodes) };
  }, [nodesById]);

  const checkboxStateFor = (id: number): CheckboxState => (
    getCheckboxState(id, checkedIds, maps.parentMap, maps.childrenMap)
  );

  const handleCheck = async (id: number, checked: boolean) => {
    setCheckFailed(false);
    let current = buildMaps();

    if (!checked) {
      const state = useCorrelationsStore.getState();
      const ancestor = nearestCheckedAncestor(id, state.standardsCheckedIds, current.parentMap);

      if (ancestor != null) {
        let fetched;

        try {
          fetched = await ensureChildrenFullyLoaded(
            queryClient,
            { grade, programId, stateId },
            explodedLevelParentIds(id, ancestor, current.parentMap),
          );
        } catch {
          setCheckFailed(true);
          return;
        }

        if (fetched.length > 0) {
          state.mergeStandardsNodes(fetched);
          current = buildMaps();
        }
      }
    }

    checkStandard(id, checked, current);
  };

  return { checkFailed, checkboxStateFor, handleCheck };
};

export default useStandardsSelection;
